/**
 * Nexus Actions Annotation Components
 *
 * Displays results from CRM and Agent management actions in chat messages
 */

'use client'

import { memo } from 'react'
import { Annotation, Annotation2 } from '@/types/annotation'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Briefcase,
  CheckCircle2,
  XCircle,
  Users,
  Mail,
  MessageSquare,
  Calendar,
  Bot,
  Sparkles,
  TrendingUp,
  Lightbulb,
  Clock,
  BarChart3,
  StickyNote,
  Phone,
  PhoneOutgoing,
  Ticket,
  CalendarPlus,
  ListTodo,
  UserPlus,
  type LucideIcon
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface NexusToolResult {
  success: boolean
  error?: string
  message?: string
  [key: string]: unknown
}

const RESULT_CARD_CLASS = 'border-0 bg-muted/20 p-3 shadow-none'
const RESULT_ERROR_CARD_CLASS = 'border-0 bg-destructive/10 p-3 shadow-none'
const RESULT_EMPTY_CARD_CLASS = 'border-0 bg-muted/25 p-3 shadow-none'
const RESULT_METRIC_CLASS = 'rounded-lg bg-background/55 px-3 py-2'
const RESULT_ROW_CLASS =
  'flex items-center justify-between gap-3 rounded-lg bg-background/55 px-3 py-2 text-xs'

function parseNexusResult(
  annotation: Annotation | Annotation2,
  toolKey: string
): NexusToolResult | null {
  const data = readAnnotationValue(annotation, toolKey)
  if (!data) return null
  const payload = isRecord(data) && isRecord(data.result) ? data.result : data

  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload)
    } catch {
      return { success: false, error: payload }
    }
  }
  return payload as NexusToolResult
}

/** Human-readable label for crm__ / nexus__ action keys (e.g. crm__log_activity -> "Log activity") */
export function getNexusToolLabel(toolKey: string): string {
  const base = toolKey.replace(/^(crm__|nexus__)/, '')
  return base
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

/** Action-specific icon, success label (user-facing in chat widgets), and optional error display. */
const NEXUS_TOOL_RESULT_CONFIG: Record<
  string,
  { icon: LucideIcon; successLabel: string; errorShowMessageOnly?: boolean }
> = {
  create_lead: { icon: UserPlus, successLabel: 'Lead created' },
  update_lead: { icon: Users, successLabel: 'Lead updated' },
  bulk_update_leads: { icon: Users, successLabel: 'Leads updated' },
  search_leads: { icon: Users, successLabel: 'Lead search complete' },
  get_lead_details: { icon: Users, successLabel: 'Lead details loaded' },
  get_email_engagement: { icon: BarChart3, successLabel: 'Email engagement ready' },
  get_lead_insights: { icon: Lightbulb, successLabel: 'Lead insights ready' },
  suggest_follow_up: { icon: Lightbulb, successLabel: 'Follow-up suggested' },
  draft_crm_email: { icon: Mail, successLabel: 'Email draft prepared' },
  draft_crm_campaign: { icon: Sparkles, successLabel: 'Campaign draft prepared' },
  create_crm_campaign: { icon: BarChart3, successLabel: 'Campaign created' },
  send_email: { icon: Mail, successLabel: 'Email sent' },
  send_sms: { icon: MessageSquare, successLabel: 'SMS sent' },
  schedule_activity: { icon: Calendar, successLabel: 'Activity scheduled' },
  log_activity: { icon: Clock, successLabel: 'Activity recorded' },
  crm_count: { icon: BarChart3, successLabel: 'Contacts count ready' },
  crm_run_query: { icon: BarChart3, successLabel: 'Contacts query ready' },
  list_agents: { icon: Bot, successLabel: 'Agents listed' },
  get_agent: { icon: Bot, successLabel: 'Agent loaded' },
  create_agent: { icon: Bot, successLabel: 'Agent created' },
  update_agent: { icon: Bot, successLabel: 'Agent updated' },
  improve_agent: { icon: Sparkles, successLabel: 'Agent prompt improved' },
  leave_note: { icon: StickyNote, successLabel: 'Note saved', errorShowMessageOnly: true },
  add_note: { icon: StickyNote, successLabel: 'Note saved', errorShowMessageOnly: true },
  schedule_appointment: { icon: Calendar, successLabel: 'Appointment scheduled' },
  book_appointment: { icon: Calendar, successLabel: 'Appointment booked' },
  book_medspa_appointment: { icon: Calendar, successLabel: 'Appointment booked' },
  book_jane_appointment: { icon: Calendar, successLabel: 'Appointment booked' },
  list_jobber_clients: { icon: Users, successLabel: 'Client list ready' },
  list_jobber_jobs: { icon: ListTodo, successLabel: 'Jobs found' },
  get_jobber_client: { icon: Users, successLabel: 'Client details loaded' },
  list_clio_matters: { icon: Briefcase, successLabel: 'Matters found' },
  list_clio_contacts: { icon: Users, successLabel: 'Contacts found' },
  get_clio_matter: { icon: Briefcase, successLabel: 'Matter details loaded' },
  book_mindbody_appointment: { icon: Calendar, successLabel: 'Appointment booked' },
  book_vagaro_appointment: { icon: Calendar, successLabel: 'Appointment booked' },
  get_available_appointment_slots: { icon: Calendar, successLabel: 'Available times found' },
  list_available_slots: { icon: Calendar, successLabel: 'Available times found' },
  list_services: { icon: ListTodo, successLabel: 'Services found' },
  create_booking: { icon: Calendar, successLabel: 'Booking created' },
  cancel_booking: { icon: Calendar, successLabel: 'Booking cancelled' },
  reschedule_booking: { icon: Calendar, successLabel: 'Booking rescheduled' },
  send_booking_confirmation: { icon: MessageSquare, successLabel: 'Confirmation sent' },
  schedule_best_available_appointment: { icon: Calendar, successLabel: 'Appointment scheduled' },
  get_upcoming_appointments: { icon: Calendar, successLabel: 'Upcoming appointments found' },
  cancel_appointment: { icon: Calendar, successLabel: 'Appointment cancelled' },
  reschedule_appointment: { icon: Calendar, successLabel: 'Appointment rescheduled' },
  create_calendar_event: { icon: CalendarPlus, successLabel: 'Added to calendar' },
  create_task: { icon: ListTodo, successLabel: 'Task added' },
  add_task: { icon: ListTodo, successLabel: 'Task added' },
  send_message: { icon: MessageSquare, successLabel: 'Message sent' },
  create_ticket: { icon: Ticket, successLabel: 'Support ticket submitted' },
  search_tickets: { icon: Ticket, successLabel: 'Tickets found' },
  make_outbound_call: { icon: PhoneOutgoing, successLabel: 'Call started' },
  initiate_call: { icon: PhoneOutgoing, successLabel: 'Call started' },
  call_number: { icon: Phone, successLabel: 'Call placed' },
  transfer_to_human: { icon: UserPlus, successLabel: 'Connected to a team member' },
  transfer_call: { icon: Phone, successLabel: 'Call transferred' },
  lookup_information: { icon: Lightbulb, successLabel: 'Information found' },
  search_info: { icon: Lightbulb, successLabel: 'Search completed' },
  get_weather: { icon: Clock, successLabel: 'Weather checked' },
  list_workspace_progress: { icon: TrendingUp, successLabel: 'Progress loaded' },
  create_workspace_progress_goal: { icon: Sparkles, successLabel: 'Progress goal created' },
  update_workspace_progress_goal: { icon: TrendingUp, successLabel: 'Progress goal updated' },
  create_workspace_progress_badges: { icon: Sparkles, successLabel: 'Progress badges created' },
  magic_generate_workspace_progress: { icon: Sparkles, successLabel: 'Progress setup created' },
  recalculate_workspace_progress: { icon: BarChart3, successLabel: 'Progress recalculated' },
  nexus_booking: { icon: Calendar, successLabel: 'Booking action complete' },

  // CRM Campaign Builder
  buildCampaignDraft: { icon: Sparkles, successLabel: 'Campaign draft prepared' },
  createCampaignFromIntent: { icon: BarChart3, successLabel: 'Campaign created' }
}

function getToolResultConfig(toolKey: string): {
  icon: LucideIcon
  successLabel: string
  errorShowMessageOnly?: boolean
} {
  const base = toolKey.replace(/^(crm__|nexus__)/, '')
  const config = NEXUS_TOOL_RESULT_CONFIG[base]
  if (config) return config
  return { icon: CheckCircle2, successLabel: getNexusToolLabel(toolKey) }
}

type NexusCrmRecord = Record<string, unknown>

function readAnnotationValue(annotation: Annotation | Annotation2, toolKey: string) {
  return (annotation as Record<string, unknown>)[toolKey]
}

function isRecord(value: unknown): value is NexusCrmRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function readRecord(record: NexusCrmRecord | null | undefined, key: string) {
  const value = record?.[key]
  return isRecord(value) ? value : null
}

function readString(record: NexusCrmRecord | null | undefined, key: string) {
  const value = record?.[key]
  return typeof value === 'string' ? value : ''
}

function readNumber(record: NexusCrmRecord | null | undefined, key: string) {
  const value = record?.[key]
  return typeof value === 'number' ? value : null
}

function readRows(value: unknown): NexusCrmRecord[] {
  if (!Array.isArray(value)) return []
  return value.filter(isRecord)
}

function humanize(value: unknown) {
  if (typeof value !== 'string' || !value) return ''
  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function getNexusCrmIcon(action: string, channel: string) {
  if (channel === 'email' || action.includes('email')) return Mail
  if (channel === 'sms' || action.includes('sms')) return MessageSquare
  if (action.includes('schedule')) return Calendar
  if (action.includes('count') || action.includes('query')) return BarChart3
  if (action.includes('insight') || action.includes('suggest')) return Lightbulb
  if (action.includes('delete')) return XCircle
  if (action.includes('create')) return UserPlus
  if (action.includes('search') || action.includes('list') || action.includes('get')) return Users
  return CheckCircle2
}

function getNexusCrmVerb(action: string) {
  if (action === 'bulk_update') return 'Updated'
  if (action === 'bulk_create') return 'Created'
  if (action === 'bulk_delete') return 'Deleted'
  if (action === 'bulk_send_email') return 'Sent email to'
  if (action === 'bulk_send_sms') return 'Sent SMS to'
  if (action === 'bulk_schedule_activity') return 'Scheduled follow-up for'
  if (action === 'bulk_log_activity') return 'Logged activity for'
  if (action === 'send_email') return 'Email sent'
  if (action === 'send_sms') return 'SMS sent'
  if (action === 'schedule_activity') return 'Activity scheduled'
  if (action === 'log_activity') return 'Activity logged'
  if (action === 'create') return 'Created'
  if (action === 'update') return 'Updated'
  if (action === 'delete') return 'Deleted'
  if (action === 'count') return 'Counted'
  if (action === 'query') return 'CRM summary ready'
  if (action === 'search') return 'Found'
  if (action === 'list') return 'Loaded'
  if (action === 'get') return 'Loaded'
  if (action === 'lead_insights') return 'Lead insights ready'
  if (action === 'suggest_follow_up') return 'Follow-up suggested'
  if (action === 'draft_email') return 'Email draft ready'
  if (action === 'draft_campaign') return 'Campaign draft ready'
  if (action === 'create_campaign') return 'Campaign created'
  if (action === 'run_campaign') return 'Campaign activated'
  if (action === 'pause_campaign') return 'Campaign paused'
  if (action === 'cancel_campaign') return 'Campaign cancelled'
  if (action === 'approve_campaign_recipients') return 'Recipients approved'
  if (action === 'reject_campaign_recipients') return 'Recipients rejected'
  if (action === 'retry_campaign_recipient') return 'Recipient queued for retry'
  if (action === 'skip_campaign_recipient') return 'Recipient skipped'
  return humanize(action) || 'CRM action complete'
}

function getNexusCrmCount(result: NexusCrmRecord, rows: NexusCrmRecord[]) {
  return (
    readNumber(result, 'count') ??
    readNumber(result, 'updated') ??
    readNumber(result, 'created') ??
    readNumber(result, 'deleted') ??
    readNumber(result, 'succeeded') ??
    readNumber(result, 'requested') ??
    readNumber(result, 'requested_count') ??
    rows.length
  )
}

function getRecordTitle(record: NexusCrmRecord) {
  const name = readString(record, 'name')
  if (name) return name

  const firstName = readString(record, 'first_name')
  const lastName = readString(record, 'last_name')
  const fullName = `${firstName} ${lastName}`.trim()
  if (fullName) return fullName

  return readString(record, 'title') || readString(record, 'subject') || readString(record, 'id')
}

function getRecordMeta(record: NexusCrmRecord) {
  return [
    readString(record, 'company'),
    readString(record, 'status'),
    readString(record, 'rating'),
    readString(record, 'stage'),
    readString(record, 'type')
  ].filter(Boolean)
}

function getNexusCrmRows(result: NexusCrmRecord) {
  const directRows = readRows(result.data)
  if (directRows.length > 0) return directRows

  return [
    ...readRows(result.leads),
    ...readRows(result.contacts),
    ...readRows(result.activities),
    ...readRows(result.results)
  ]
}

function getNexusCrmPrimaryRecord(result: NexusCrmRecord) {
  if (isRecord(result.data)) return result.data
  if (isRecord(result.lead)) return result.lead
  if (isRecord(result.contact)) return result.contact
  if (isRecord(result.activity)) return result.activity
  if (isRecord(result.campaign)) return result.campaign
  return null
}

function NexusCrmInlineRows({ rows }: { rows: NexusCrmRecord[] }) {
  if (rows.length === 0) return null

  return (
    <div className="mt-2 space-y-1.5 text-sm">
      {rows.slice(0, 5).map((row, index) => {
        const title = getRecordTitle(row)
        const meta = getRecordMeta(row)
        const id = readString(row, 'id')

        return (
          <div
            key={id || index}
            className="flex items-center gap-3 rounded-lg bg-background/55 px-3 py-2"
          >
            <span className="min-w-0 flex-1 truncate">{title || `Record ${index + 1}`}</span>
            {meta.slice(0, 2).map((item) => (
              <span
                key={item}
                className="shrink-0 rounded-full bg-muted/70 px-2 py-0.5 text-[11px] text-muted-foreground"
              >
                {item}
              </span>
            ))}
          </div>
        )
      })}
      {rows.length > 5 && (
        <div className="py-2 text-xs text-muted-foreground">+{rows.length - 5} more</div>
      )}
    </div>
  )
}

export const NexusCrmUnifiedAnnotation = memo(
  ({ annotation, toolKey }: { annotation: Annotation | Annotation2; toolKey: string }) => {
    const result = parseNexusResult(annotation, toolKey)
    if (!result) return null

    const action = readString(result, 'action')
    const entity = readString(result, 'entity')
    const channel = readString(result, 'channel')
    const rows = getNexusCrmRows(result)
    const primaryRecord = getNexusCrmPrimaryRecord(result)
    const count = getNexusCrmCount(result, rows)
    const Icon = getNexusCrmIcon(action, channel)
    const message = readString(result, 'message')
    const requiresConfirmation = result.requires_confirmation === true
    const failed = result.success === false && !requiresConfirmation
    const title = requiresConfirmation
      ? `Confirm ${humanize(action).toLowerCase()}`
      : failed
        ? `${humanize(action) || 'CRM action'} failed`
        : `${getNexusCrmVerb(action)}${entity ? ` ${humanize(entity).toLowerCase()}` : ''}`

    if (requiresConfirmation) {
      return (
        <div className="not-prose rounded-lg bg-amber-500/10 px-3 py-2">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <span className="font-medium">{title}</span>
            {count > 0 && (
              <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-700 dark:text-amber-300">
                {count} affected
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Reply with approval and I’ll run it with confirmation.
          </p>
        </div>
      )
    }

    if (failed) {
      return (
        <div className="not-prose rounded-lg bg-destructive/10 px-3 py-2">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4 shrink-0" />
            <span>{result.error || message || 'CRM action failed'}</span>
          </div>
        </div>
      )
    }

    return (
      <div className="not-prose rounded-lg bg-muted/20 px-3 py-2">
        <div className="flex items-center gap-2 text-sm">
          <Icon className="size-4 shrink-0 text-primary" />
          <span className="font-medium">{title}</span>
          {count > 0 && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
              {count}
            </span>
          )}
        </div>
        {message && <p className="mt-1 text-xs text-muted-foreground">{message}</p>}
        {primaryRecord && rows.length === 0 && <NexusCrmInlineRows rows={[primaryRecord]} />}
        <NexusCrmInlineRows rows={rows} />
      </div>
    )
  }
)
NexusCrmUnifiedAnnotation.displayName = 'NexusCrmUnifiedAnnotation'

/**
 * Unified result card for Nexus/CRM actions. Uses action-specific icon and label so each
 * annotation looks intentional and matches the quality of dedicated components.
 */
export const NexusToolResultCard = memo(
  ({ annotation, toolKey }: { annotation: Annotation | Annotation2; toolKey: string }) => {
    const raw = readAnnotationValue(annotation, toolKey)
    if (raw === undefined || raw === null) return null
    const payload = isRecord(raw) && 'result' in raw ? raw.result : raw
    let result: NexusToolResult | unknown[] = payload as NexusToolResult | unknown[]
    if (typeof payload === 'string') {
      try {
        result = JSON.parse(payload) as NexusToolResult
      } catch {
        result = { success: false, error: payload }
      }
    }
    const { icon: Icon, successLabel, errorShowMessageOnly } = getToolResultConfig(toolKey)
    const actionLabel = getNexusToolLabel(toolKey)
    const obj =
      result && typeof result === 'object' && !Array.isArray(result)
        ? (result as Record<string, unknown>)
        : null
    const requiresConfirmation =
      obj?.requiresConfirmation === true || obj?.requires_confirmation === true
    if (requiresConfirmation) {
      return (
        <Card className="border-0 bg-amber-500/10 p-3 shadow-none">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <span className="font-medium">Confirm {actionLabel.toLowerCase()}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {typeof obj?.message === 'string'
              ? obj.message
              : 'Reply with approval and I’ll run it with confirmation.'}
          </p>
        </Card>
      )
    }
    const isError = Boolean(obj && (obj.success === false || obj.error != null))
    if (isError) {
      const errMsg = typeof obj?.error === 'string' ? obj.error : 'Unknown error'
      const errorText = errorShowMessageOnly ? errMsg : `${actionLabel} failed: ${errMsg}`
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4 shrink-0" />
            <span>{errorText}</span>
          </div>
        </Card>
      )
    }
    const count = Array.isArray(result)
      ? result.length
      : (readNumber(obj, 'count') ?? readNumber(obj, 'total'))
    const id =
      readString(obj, 'id') || readString(obj, 'activity_id') || readString(obj, 'ticket_id')
    const name =
      readString(obj, 'name') ||
      readString(readRecord(obj, 'agent'), 'name') ||
      readString(readRecord(obj, 'lead'), 'name')
    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm">
          <Icon className="size-4 shrink-0 text-primary" />
          <span className="font-medium">{successLabel}</span>
          {count != null && count > 0 && (
            <Badge variant="secondary" className="ml-auto border-0 text-xs">
              {count} {Array.isArray(result) ? 'items' : 'result'}
            </Badge>
          )}
          {id && !count && (
            <Badge variant="secondary" className="ml-auto border-0 text-xs">
              {String(id).slice(0, 8)}
            </Badge>
          )}
          {name && !count && (
            <Badge variant="secondary" className="ml-auto border-0 text-xs">
              {name}
            </Badge>
          )}
        </div>
        {obj?.message != null ? (
          <p className="mt-1 text-xs text-muted-foreground">{String(obj.message)}</p>
        ) : null}
      </Card>
    )
  }
)
NexusToolResultCard.displayName = 'NexusToolResultCard'

const PROGRESS_TOOL_NAMES = new Set([
  'list_workspace_progress',
  'create_workspace_progress_goal',
  'update_workspace_progress_goal',
  'create_workspace_progress_badges',
  'magic_generate_workspace_progress',
  'recalculate_workspace_progress'
])

function readProgressLevel(result: NexusToolResult) {
  const level = isRecord(result.level) ? result.level : null

  return {
    currentValue: readNumber(level, 'currentValue') ?? 0,
    level: readNumber(level, 'level') ?? 1,
    maxValue: readNumber(level, 'maxValue') ?? 100,
    totalPoints: readNumber(level, 'totalPoints') ?? 0
  }
}

function getProgressRows(result: NexusToolResult, key: string) {
  return readRows(result[key]).slice(0, 4)
}

export const NexusProgressAnnotation = memo(
  ({ annotation, toolKey }: { annotation: Annotation | Annotation2; toolKey: string }) => {
    const result = parseNexusResult(annotation, toolKey)
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4 shrink-0" />
            <span>{result.error || 'Progress action failed'}</span>
          </div>
        </Card>
      )
    }

    const level = readProgressLevel(result)
    const goals = getProgressRows(result, 'goals')
    const badges = getProgressRows(result, 'badges')
    const action = readString(result, 'action')
    const title =
      action === 'magic_generate'
        ? 'Progress setup created'
        : action === 'create_goal'
          ? 'Progress goal created'
          : action === 'update_goal'
            ? 'Progress goal updated'
            : action === 'create_badges'
              ? 'Progress badges ready'
              : action === 'recalculate'
                ? 'Progress recalculated'
                : 'Progress loaded'

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <TrendingUp className="size-4 shrink-0 text-primary" />
          <span className="font-medium">{title}</span>
          <Badge variant="secondary" className="ml-auto border-0 text-xs">
            Level {level.level}
          </Badge>
        </div>
        {result.message ? (
          <p className="mt-1 text-xs text-muted-foreground">{String(result.message)}</p>
        ) : null}
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <div className={RESULT_METRIC_CLASS}>
            <div className="text-[11px] text-muted-foreground">Points</div>
            <div className="text-sm font-semibold tabular-nums">{level.totalPoints}</div>
          </div>
          <div className={RESULT_METRIC_CLASS}>
            <div className="text-[11px] text-muted-foreground">Level progress</div>
            <div className="text-sm font-semibold tabular-nums">
              {level.currentValue}/{level.maxValue}
            </div>
          </div>
          <div className={RESULT_METRIC_CLASS}>
            <div className="text-[11px] text-muted-foreground">Goals</div>
            <div className="text-sm font-semibold tabular-nums">{goals.length}</div>
          </div>
        </div>
        {goals.length > 0 ? (
          <div className="mt-3 space-y-1.5 text-xs">
            {goals.map((goal, index) => (
              <div
                key={readString(goal, 'id') || index}
                className="flex items-center gap-2 rounded-lg bg-background/55 px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate">
                  {readString(goal, 'name') || `Goal ${index + 1}`}
                </span>
                <span className="shrink-0 text-muted-foreground">
                  {readNumber(goal, 'current') ?? 0}/{readNumber(goal, 'target') ?? 0}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {badges.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {badges.map((badge, index) => (
              <Badge
                key={readString(badge, 'id') || index}
                variant="secondary"
                className="border-0 text-xs"
              >
                {readString(badge, 'name') || 'Badge'}
              </Badge>
            ))}
          </div>
        ) : null}
      </Card>
    )
  }
)
NexusProgressAnnotation.displayName = 'NexusProgressAnnotation'

/** Generic Nexus/CRM renderer with a native inline path for the unified CRM tool. */
export const GenericNexusCrmAnnotation = memo(
  ({ annotation, toolKey }: { annotation: Annotation | Annotation2; toolKey: string }) => {
    const base = toolKey.replace(/^(crm__|nexus__)/, '')

    if (base === 'nexus_crm' || base === 'NexusCRMTool__run') {
      return <NexusCrmUnifiedAnnotation annotation={annotation} toolKey={toolKey} />
    }

    if (PROGRESS_TOOL_NAMES.has(base)) {
      return <NexusProgressAnnotation annotation={annotation} toolKey={toolKey} />
    }

    return <NexusToolResultCard annotation={annotation} toolKey={toolKey} />
  }
)
GenericNexusCrmAnnotation.displayName = 'GenericNexusCrmAnnotation'

// CRM Lead Actions
export const CrmCreateLeadAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'crm__create_lead')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to create lead: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    const lead = (result as any).agent || (result as any).lead
    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="size-4 text-primary" />
          <span className="font-medium">Lead created</span>
          {lead?.id && (
            <Badge variant="secondary" className="ml-auto border-0 text-xs">
              {lead.name || `${lead.first_name} ${lead.last_name}`}
            </Badge>
          )}
        </div>
        {result.message && <p className="mt-1 text-xs text-muted-foreground">{result.message}</p>}
      </Card>
    )
  }
)
CrmCreateLeadAnnotation.displayName = 'CrmCreateLeadAnnotation'

export const CrmUpdateLeadAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'crm__update_lead')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to update lead: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    const lead = (result as any).agent
    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="size-4 text-primary" />
          <span className="font-medium">Lead updated</span>
          {lead?.id && (
            <Badge variant="secondary" className="ml-auto border-0 text-xs">
              {lead.name || lead.id.slice(0, 8)}
            </Badge>
          )}
        </div>
        {result.message && <p className="mt-1 text-xs text-muted-foreground">{result.message}</p>}
      </Card>
    )
  }
)
CrmUpdateLeadAnnotation.displayName = 'CrmUpdateLeadAnnotation'

export const CrmGetLeadDetailsAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'crm__get_lead_details')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to load lead details: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    const lead = (result as any).lead
    const activities = (result as any).activities || []
    const activityCount = (result as any).activity_count || 0

    if (!lead) return null

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Users className="size-4 text-primary" />
              <span className="min-w-0 truncate font-medium">{lead.name}</span>
              <Badge variant="secondary" className="border-0 text-xs">
                {lead.status}
              </Badge>
              {lead.rating && lead.rating !== 'unrated' && (
                <Badge variant="secondary" className="border-0 text-xs">
                  {lead.rating}
                </Badge>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            {lead.company && (
              <div>
                <span className="text-muted-foreground">Company:</span> {lead.company}
              </div>
            )}
            {lead.email && (
              <div>
                <span className="text-muted-foreground">Email:</span> {lead.email}
              </div>
            )}
            {lead.phone && (
              <div>
                <span className="text-muted-foreground">Phone:</span> {lead.phone}
              </div>
            )}
            {lead.title && (
              <div>
                <span className="text-muted-foreground">Title:</span> {lead.title}
              </div>
            )}
          </div>

          {activityCount > 0 && (
            <div className="mt-2 rounded-lg bg-background/55 px-3 py-2">
              <div className="mb-1 flex items-center justify-between text-xs font-medium">
                <span>Activity Log ({activityCount})</span>
              </div>
              <div className="max-h-32 space-y-1 overflow-y-auto">
                {activities.slice(0, 5).map((activity: any) => (
                  <div key={activity.id} className="flex items-start gap-2 text-xs">
                    <div className="mt-0.5 size-1.5 rounded-full bg-primary/60" />
                    <div className="flex-1">
                      <div className="font-medium">{activity.title}</div>
                      {activity.description && (
                        <div className="line-clamp-1 text-muted-foreground">
                          {activity.description}
                        </div>
                      )}
                      <div className="text-[10px] text-muted-foreground">
                        {new Date(activity.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
                {activityCount > 5 && (
                  <p className="text-[10px] text-muted-foreground">
                    +{activityCount - 5} more activities
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>
    )
  }
)
CrmGetLeadDetailsAnnotation.displayName = 'CrmGetLeadDetailsAnnotation'

export const CrmSearchLeadsAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'crm__search_leads')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Search failed: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    const leads = (result as any).leads || []
    const count = (result as any).count || leads.length

    if (count === 0) {
      return (
        <Card className={RESULT_EMPTY_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="size-4" />
            <span>No leads found</span>
          </div>
        </Card>
      )
    }

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm font-medium">
          <Users className="size-4 text-primary" />
          <span>
            Found {count} lead{count !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="mt-2 space-y-1.5">
          {leads.slice(0, 5).map((lead: any) => (
            <div key={lead.id} className={RESULT_ROW_CLASS}>
              <span className="min-w-0 flex-1 truncate font-medium">{lead.name}</span>
              <div className="flex gap-1">
                {lead.email && (
                  <Badge variant="secondary" className="border-0 text-[10px]">
                    <Mail className="mr-1 size-2.5" />
                    Email
                  </Badge>
                )}
                {lead.status && (
                  <Badge variant="secondary" className="border-0 text-[10px]">
                    {lead.status}
                  </Badge>
                )}
              </div>
            </div>
          ))}
          {count > 5 && (
            <p className="text-[10px] text-muted-foreground">
              +{count - 5} more lead{count - 5 !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </Card>
    )
  }
)
CrmSearchLeadsAnnotation.displayName = 'CrmSearchLeadsAnnotation'

export const CrmSendEmailAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'crm__send_email')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Email failed: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm">
          <Mail className="size-4 text-primary" />
          <span className="font-medium">Email sent</span>
          {(result as any).message_id && (
            <Badge variant="secondary" className="ml-auto border-0 text-xs">
              {(result as any).message_id.slice(0, 8)}
            </Badge>
          )}
        </div>
        {result.message && <p className="mt-1 text-xs text-muted-foreground">{result.message}</p>}
      </Card>
    )
  }
)
CrmSendEmailAnnotation.displayName = 'CrmSendEmailAnnotation'

export const CrmSendSmsAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'crm__send_sms')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>SMS failed: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm">
          <MessageSquare className="size-4 text-primary" />
          <span className="font-medium">SMS sent</span>
          {(result as any).message_id && (
            <Badge variant="secondary" className="ml-auto border-0 text-xs">
              {(result as any).message_id.slice(0, 8)}
            </Badge>
          )}
        </div>
        {result.message && <p className="mt-1 text-xs text-muted-foreground">{result.message}</p>}
      </Card>
    )
  }
)
CrmSendSmsAnnotation.displayName = 'CrmSendSmsAnnotation'

export const CrmGetEmailEngagementAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'crm__get_email_engagement')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to analyze engagement: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    const metrics = (result as any).metrics || {}
    const insights = (result as any).insights || []

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <BarChart3 className="size-4 text-primary" />
            <span>Email Engagement Analysis</span>
            <Badge
              variant={metrics.engagement_score >= 70 ? 'default' : 'secondary'}
              className="ml-auto border-0 text-xs"
            >
              {metrics.engagement_score}/100
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-muted-foreground">Open Rate:</span> {metrics.open_rate}%
            </div>
            <div>
              <span className="text-muted-foreground">Click Rate:</span> {metrics.click_rate}%
            </div>
            <div>
              <span className="text-muted-foreground">Reply Rate:</span> {metrics.reply_rate}%
            </div>
            <div>
              <span className="text-muted-foreground">Total Emails:</span>{' '}
              {metrics.total_emails_sent}
            </div>
          </div>

          {insights.length > 0 && (
            <div className="mt-2 rounded-lg bg-background/55 px-3 py-2">
              <div className="mb-1 flex items-center gap-1 text-xs font-medium">
                <Lightbulb className="size-3" />
                <span>Insights</span>
              </div>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {insights.map((insight: string, i: number) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="mt-0.5">•</span>
                    <span>{insight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Card>
    )
  }
)
CrmGetEmailEngagementAnnotation.displayName = 'CrmGetEmailEngagementAnnotation'

export const CrmGetLeadInsightsAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'crm__get_lead_insights')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to generate insights: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    const healthScore = (result as any).health_score || 0
    const healthStatus = (result as any).health_status || 'unknown'
    const metrics = (result as any).metrics || {}
    const recommendations = (result as any).recommendations || []

    const statusColors: Record<string, string> = {
      excellent: 'text-emerald-500',
      good: 'text-primary',
      fair: 'text-amber-500',
      poor: 'text-destructive'
    }

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <TrendingUp className="size-4 text-primary" />
              <span>Lead Health Score</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`text-lg font-bold ${statusColors[healthStatus] || ''}`}>
                {healthScore}/100
              </div>
              <Badge
                variant={healthStatus === 'excellent' ? 'default' : 'secondary'}
                className="border-0 text-xs"
              >
                {healthStatus}
              </Badge>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-muted-foreground">Status:</span> {metrics.status}
            </div>
            <div>
              <span className="text-muted-foreground">Rating:</span> {metrics.rating}
            </div>
            <div>
              <span className="text-muted-foreground">Activities:</span> {metrics.activity_count}
            </div>
            <div>
              <span className="text-muted-foreground">Data Complete:</span>{' '}
              {metrics.data_completeness}%
            </div>
          </div>

          {recommendations.length > 0 && (
            <div className="mt-2 rounded-lg bg-background/55 px-3 py-2">
              <div className="mb-1 flex items-center gap-1 text-xs font-medium">
                <Lightbulb className="size-3" />
                <span>Recommendations</span>
              </div>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {recommendations.map((rec: string, i: number) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="mt-0.5">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Card>
    )
  }
)
CrmGetLeadInsightsAnnotation.displayName = 'CrmGetLeadInsightsAnnotation'

export const CrmSuggestFollowUpAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'crm__suggest_follow_up')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to suggest follow-up: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    const suggestedTiming = (result as any).suggested_timing || ''
    const urgency = (result as any).urgency || 'normal'
    const timingReason = (result as any).timing_reason || ''
    const contentSuggestions = (result as any).content_suggestions || []
    const channelSuggestions = (result as any).channel_suggestions || []

    const urgencyColors: Record<string, string> = {
      high: 'text-destructive',
      medium: 'text-amber-500',
      normal: 'text-primary'
    }

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Clock className="size-4 text-primary" />
              <span>Follow-up Suggestion</span>
            </div>
            <Badge
              variant={urgency === 'high' ? 'destructive' : 'secondary'}
              className="border-0 text-xs"
            >
              {urgency} priority
            </Badge>
          </div>

          <div className="space-y-2 text-xs">
            <div>
              <span className="font-medium text-muted-foreground">Suggested Timing:</span>{' '}
              <span className={urgencyColors[urgency] || ''}>{suggestedTiming}</span>
            </div>
            {timingReason && <div className="text-muted-foreground">{timingReason}</div>}
          </div>

          {contentSuggestions.length > 0 && (
            <div className="mt-2 rounded-lg bg-background/55 px-3 py-2">
              <div className="mb-1 text-xs font-medium">Content Suggestions</div>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {contentSuggestions.slice(0, 3).map((suggestion: string, i: number) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="mt-0.5">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {channelSuggestions.length > 0 && (
            <div className="mt-2 rounded-lg bg-background/55 px-3 py-2">
              <div className="mb-1 text-xs font-medium">Channel Suggestions</div>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {channelSuggestions.map((suggestion: string, i: number) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="mt-0.5">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Card>
    )
  }
)
CrmSuggestFollowUpAnnotation.displayName = 'CrmSuggestFollowUpAnnotation'

export const CrmScheduleActivityAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'crm__schedule_activity')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to schedule: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="size-4 text-primary" />
          <span className="font-medium">Activity scheduled</span>
          {(result as any).activity_id && (
            <Badge variant="secondary" className="ml-auto border-0 text-xs">
              {(result as any).activity_id.slice(0, 8)}
            </Badge>
          )}
        </div>
        {result.message && <p className="mt-1 text-xs text-muted-foreground">{result.message}</p>}
      </Card>
    )
  }
)
CrmScheduleActivityAnnotation.displayName = 'CrmScheduleActivityAnnotation'

// Agent Management Actions
export const NexusListAgentsAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'nexus__list_agents')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to list agents: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    const agents = (result as any).agents || []
    const total = (result as any).total || agents.length

    if (total === 0) {
      return (
        <Card className={RESULT_EMPTY_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Bot className="size-4" />
            <span>No agents found</span>
          </div>
        </Card>
      )
    }

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm font-medium">
          <Bot className="size-4 text-primary" />
          <span>
            {total} agent{total !== 1 ? 's' : ''} found
          </span>
        </div>
        <div className="mt-2 space-y-1.5">
          {agents.slice(0, 5).map((agent: any) => (
            <div key={agent.id} className={RESULT_ROW_CLASS}>
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <div
                  className={cn(
                    'size-2 rounded-full',
                    agent.status === 'active'
                      ? 'bg-emerald-500'
                      : agent.status === 'inactive'
                        ? 'bg-muted-foreground/60'
                        : 'bg-amber-500'
                  )}
                />
                <span className="min-w-0 truncate font-medium">{agent.name}</span>
              </div>
              <div className="flex gap-1">
                <Badge variant="secondary" className="border-0 text-[10px]">
                  {agent.type}
                </Badge>
                {agent.status && (
                  <Badge variant="secondary" className="border-0 text-[10px]">
                    {agent.status}
                  </Badge>
                )}
              </div>
            </div>
          ))}
          {total > 5 && (
            <p className="text-[10px] text-muted-foreground">
              +{total - 5} more agent{total - 5 !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </Card>
    )
  }
)
NexusListAgentsAnnotation.displayName = 'NexusListAgentsAnnotation'

export const NexusGetAgentAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'nexus__get_agent')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to load agent: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    const agent = (result as any).agent
    if (!agent) return null

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
          <Bot className="size-4 text-primary" />
          <span className="min-w-0 truncate">{agent.name}</span>
          <Badge variant="secondary" className="border-0 text-xs">
            {agent.type}
          </Badge>
          <Badge
            variant={agent.status === 'active' ? 'default' : 'secondary'}
            className="border-0 text-xs"
          >
            {agent.status}
          </Badge>
        </div>
        {agent.system_prompt && (
          <div className="mt-2 rounded-lg bg-background/55 px-3 py-2 text-xs">
            <p className="line-clamp-2 text-muted-foreground">{agent.system_prompt}</p>
          </div>
        )}
        {agent.capabilities && (
          <div className="mt-2 flex flex-wrap gap-1">
            {Object.entries(agent.capabilities as Record<string, boolean>)
              .filter(([, enabled]) => enabled)
              .slice(0, 5)
              .map(([key]) => (
                <Badge key={key} variant="secondary" className="border-0 text-[10px]">
                  {key.replace(/_/g, ' ')}
                </Badge>
              ))}
          </div>
        )}
      </Card>
    )
  }
)
NexusGetAgentAnnotation.displayName = 'NexusGetAgentAnnotation'

export const NexusCreateAgentAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'nexus__create_agent')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to create agent: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    const agent = (result as any).agent
    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="size-4 text-primary" />
          <span className="font-medium">Agent created</span>
          {agent?.id && (
            <Badge variant="secondary" className="ml-auto border-0 text-xs">
              {agent.name || agent.id.slice(0, 8)}
            </Badge>
          )}
        </div>
        {result.message && <p className="mt-1 text-xs text-muted-foreground">{result.message}</p>}
      </Card>
    )
  }
)
NexusCreateAgentAnnotation.displayName = 'NexusCreateAgentAnnotation'

export const NexusUpdateAgentAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'nexus__update_agent')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to update agent: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    const agent = (result as any).agent
    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm">
          <Bot className="size-4 text-primary" />
          <span className="font-medium">Agent updated</span>
          {agent?.id && (
            <Badge variant="secondary" className="ml-auto border-0 text-xs">
              {agent.name || agent.id.slice(0, 8)}
            </Badge>
          )}
        </div>
        {result.message && <p className="mt-1 text-xs text-muted-foreground">{result.message}</p>}
      </Card>
    )
  }
)
NexusUpdateAgentAnnotation.displayName = 'NexusUpdateAgentAnnotation'

export const NexusImproveAgentAnnotation = memo(
  ({ annotation }: { annotation: Annotation | Annotation2 }) => {
    const result = parseNexusResult(annotation, 'nexus__improve_agent')
    if (!result) return null

    if (!result.success) {
      return (
        <Card className={RESULT_ERROR_CARD_CLASS}>
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="size-4" />
            <span>Failed to improve agent: {result.error || 'Unknown error'}</span>
          </div>
        </Card>
      )
    }

    return (
      <Card className={RESULT_CARD_CLASS}>
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="size-4 text-primary" />
          <span className="font-medium">Agent improved</span>
          {(result as any).improvement && (
            <Badge variant="secondary" className="ml-auto border-0 text-xs">
              {(result as any).improvement.replace(/_/g, ' ')}
            </Badge>
          )}
        </div>
        {result.message && <p className="mt-1 text-xs text-muted-foreground">{result.message}</p>}
      </Card>
    )
  }
)
NexusImproveAgentAnnotation.displayName = 'NexusImproveAgentAnnotation'
